// System 4 — signage and holographics.
//
// The brief's one carve-out from the zero-asset rule: "signage text may be rasterized at runtime to
// a canvas with system fonts and baked into a texture. That's generated in code, not shipped as an
// asset." Everything below honours that exactly -- names, layouts, glyph rendering and glow are all
// computed at boot into one canvas. No font files, no images.
//
// ONE ATLAS, ONE DRAW CALL. Sixty-four distinct signs are rasterised into a single 2048x2048 canvas
// and every sign in the city is a quad indexed into it. A separate material per sign would be sixty
// extra draw calls for the most numerous object on the street.
//
// A sign is also a LIGHT. The System 3 critic's routing note was that lit signs are simultaneously
// the missing subject and the missing light source, so every sign placed also registers a source
// through the same `B.source()` path the lamps use -- the light and the thing that emits it are one
// record, and cannot drift apart.

import * as THREE from 'three';
import { rngFor, hashFloat } from './rng.js';

export const ATLAS_DIM = 2048;
export const GRID = 8;                      // 8x8 cells
export const CELL = ATLAS_DIM / GRID;       // 256 px

// ---------------------------------------------------------------------------
// Procedural names. Syllables, not a word list -- a word list is an asset.
// ---------------------------------------------------------------------------
const HEAD = ['NOVA', 'KIRI', 'AZUL', 'HOSHI', 'VERT', 'RAKU', 'ONYX', 'SANO', 'DELT', 'MIRA',
  'KABU', 'ZEN', 'IZU', 'ORBIT', 'TOKO', 'HALO', 'RIO', 'SUMI', 'VEGA', 'ANKA'];
const TAIL = ['MART', 'RAMEN', 'BAR', 'LAB', 'CLINIC', 'PARTS', 'NOODLE', 'CYBER', 'WASH', 'LOAN',
  'PAWN', 'DINER', 'TECH', 'GRILL', 'OPTIC', 'KARAOKE', 'MOTEL', 'ARCADE', 'CHEM', 'SUSHI'];
const SOLO = ['OPEN 24H', 'VACANCY', 'NO VACANCY', 'CASH ONLY', 'PARKING', 'EXIT', 'LOADING',
  'PHARMACY', 'HOT FOOD', 'LIVE', 'SALE', 'ID CHECK', 'REPAIRS', 'TATTOO', 'FUEL'];

/** Palette biased the way real signage is: saturated primaries, plus a lot of white and warm. */
const INK = [
  '#ff2d55', '#ff7a1a', '#ffd400', '#3dff9e', '#28d7ff', '#4a7bff', '#c05cff', '#ff4fa3',
  '#ffffff', '#ffd9a8', '#a8f0ff', '#ff5b3a',
];

function pickName(r) {
  const roll = r.f();
  if (roll < 0.22) return r.pick(SOLO);
  if (roll < 0.72) return `${r.pick(HEAD)} ${r.pick(TAIL)}`;
  return r.pick(HEAD);
}

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------
function fitFont(ctx, text, maxW, maxH, family, weight) {
  let size = maxH;
  for (let i = 0; i < 24; i++) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxW) break;
    size *= 0.92;
  }
  return size;
}

/**
 * Neon: a stroked outline with a bright core and a wide bloom, drawn as several passes of
 * decreasing width. This is how a real tube reads -- the glass core is nearly white and the colour
 * lives in the halo around it, which is why simply filling a glyph with pink never looks like neon.
 */
function drawNeon(ctx, r, x0, y0, w, h) {
  const text = pickName(r);
  const ink = r.pick(INK);
  const fam = r.chance(0.5) ? 'Impact, "Arial Black", sans-serif' : '"Trebuchet MS", Arial, sans-serif';
  const size = fitFont(ctx, text, w * 0.86, h * 0.44, fam, '700');
  ctx.font = `700 ${size}px ${fam}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = x0 + w / 2, cy = y0 + h / 2;

  // Dead-tube segments: a hard clip band that kills part of the glyph run.
  const dead = r.chance(0.30);
  const deadX = x0 + r.range(0.1, 0.75) * w, deadW = r.range(0.06, 0.20) * w;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, y0, w, h);
  if (dead) {
    // Everything except the dead band.
    ctx.rect(deadX + deadW, y0, w, h);
  }
  ctx.clip();

  ctx.lineJoin = 'round';
  for (const [width, alpha, colour] of [
    [size * 0.42, 0.10, ink], [size * 0.26, 0.16, ink], [size * 0.15, 0.30, ink],
    [size * 0.075, 0.75, ink], [size * 0.028, 1.0, '#ffffff'],
  ]) {
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.strokeText(text, cx, cy);
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // Mounting: a dim tube path where the dead segment is, so the glass is still there unlit.
  if (dead) {
    ctx.save();
    ctx.beginPath(); ctx.rect(deadX, y0, deadW, h); ctx.clip();
    ctx.globalAlpha = 0.16;
    ctx.strokeStyle = '#6a6f78';
    ctx.lineWidth = size * 0.03;
    ctx.strokeText(text, cx, cy);
    ctx.globalAlpha = 1;
    ctx.restore();
  }
  return { ink, kind: 'neon', dead };
}

/** Internally-lit lightbox: bright ground, dark type, a border, and grime in the corners. */
function drawBox(ctx, r, x0, y0, w, h) {
  const ink = r.pick(INK);
  const g = ctx.createLinearGradient(x0, y0, x0, y0 + h);
  g.addColorStop(0, '#fffdf4'); g.addColorStop(0.55, '#f4ead2'); g.addColorStop(1, '#d9cdb4');
  ctx.fillStyle = g;
  ctx.fillRect(x0 + 6, y0 + 6, w - 12, h - 12);
  ctx.strokeStyle = '#22242a';
  ctx.lineWidth = 7;
  ctx.strokeRect(x0 + 6, y0 + 6, w - 12, h - 12);

  const text = pickName(r);
  const fam = '"Arial Narrow", Arial, sans-serif';
  const size = fitFont(ctx, text, w * 0.80, h * 0.40, fam, '700');
  ctx.font = `700 ${size}px ${fam}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = r.chance(0.6) ? '#1a1c22' : ink;
  ctx.fillText(text, x0 + w / 2, y0 + h * 0.48);

  // A second line of smaller type on some boxes.
  if (r.chance(0.55)) {
    const sub = r.pick(SOLO);
    const s2 = fitFont(ctx, sub, w * 0.6, h * 0.16, fam, '400');
    ctx.font = `400 ${s2}px ${fam}`;
    ctx.fillStyle = '#3a3d45';
    ctx.fillText(sub, x0 + w / 2, y0 + h * 0.74);
  }
  // Grime pooling in the corners of the diffuser.
  for (const [gx, gy] of [[x0 + 10, y0 + 10], [x0 + w - 10, y0 + 10], [x0 + 10, y0 + h - 10], [x0 + w - 10, y0 + h - 10]]) {
    const rg = ctx.createRadialGradient(gx, gy, 0, gx, gy, w * 0.30);
    rg.addColorStop(0, 'rgba(24,20,14,0.42)'); rg.addColorStop(1, 'rgba(24,20,14,0)');
    ctx.fillStyle = rg;
    ctx.fillRect(x0, y0, w, h);
  }
  return { ink: '#f4ead2', kind: 'box', dead: false };
}

/** Vertical blade: characters stacked down the sign, which is what a projecting sign usually is. */
function drawBlade(ctx, r, x0, y0, w, h) {
  const ink = r.pick(INK);
  ctx.fillStyle = '#0b0d11';
  ctx.fillRect(x0 + 4, y0 + 4, w - 8, h - 8);
  ctx.strokeStyle = '#2c2f36'; ctx.lineWidth = 5;
  ctx.strokeRect(x0 + 4, y0 + 4, w - 8, h - 8);

  const word = r.pick(HEAD);
  const n = word.length;
  const cellH = (h - 24) / n;
  const fam = 'Impact, "Arial Black", sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  for (let i = 0; i < n; i++) {
    const size = Math.min(cellH * 0.86, w * 0.62);
    ctx.font = `700 ${size}px ${fam}`;
    const cy = y0 + 12 + cellH * (i + 0.5);
    // Same tube construction as the neon renderer.
    ctx.lineJoin = 'round';
    for (const [lw, a, c] of [[size * 0.30, 0.14, ink], [size * 0.15, 0.34, ink],
      [size * 0.07, 0.8, ink], [size * 0.025, 1, '#ffffff']]) {
      ctx.globalAlpha = a; ctx.strokeStyle = c; ctx.lineWidth = lw;
      ctx.strokeText(word[i], x0 + w / 2, cy);
    }
    ctx.globalAlpha = 1;
  }
  return { ink, kind: 'blade', dead: false };
}

/** Holographic panel: scanlines, a colour-split ghost, and a soft edge falloff. */
function drawHolo(ctx, r, x0, y0, w, h) {
  const ink = r.pick(['#28d7ff', '#4a7bff', '#3dff9e', '#c05cff']);
  ctx.fillStyle = 'rgba(6,14,22,0.55)';
  ctx.fillRect(x0, y0, w, h);
  const text = pickName(r);
  const fam = '"Trebuchet MS", Arial, sans-serif';
  const size = fitFont(ctx, text, w * 0.8, h * 0.30, fam, '400');
  ctx.font = `400 ${size}px ${fam}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  // Chromatic ghosting, which is what sells a projected image.
  ctx.globalAlpha = 0.55; ctx.fillStyle = '#ff3b6b';
  ctx.fillText(text, x0 + w / 2 - size * 0.045, y0 + h * 0.42);
  ctx.fillStyle = '#3bffe0';
  ctx.fillText(text, x0 + w / 2 + size * 0.045, y0 + h * 0.42);
  ctx.globalAlpha = 1; ctx.fillStyle = ink;
  ctx.fillText(text, x0 + w / 2, y0 + h * 0.42);

  // A block of smaller running type underneath.
  ctx.font = `400 ${size * 0.30}px ${fam}`;
  ctx.globalAlpha = 0.72;
  for (let i = 0; i < 3; i++) {
    ctx.fillText(r.pick(SOLO), x0 + w / 2, y0 + h * (0.62 + i * 0.11));
  }
  ctx.globalAlpha = 1;

  // Scanlines.
  ctx.fillStyle = 'rgba(0,0,0,0.34)';
  for (let y = y0; y < y0 + h; y += 5) ctx.fillRect(x0, y, w, 2);
  return { ink, kind: 'holo', dead: false };
}

/** Fascia band: wide shop name over the shopfront head. */
function drawFascia(ctx, r, x0, y0, w, h) {
  const ink = r.pick(INK);
  const bg = r.pick(['#12141a', '#1b1410', '#101a18', '#1a1018']);
  ctx.fillStyle = bg; ctx.fillRect(x0, y0, w, h);
  const text = `${r.pick(HEAD)} ${r.pick(TAIL)}`;
  const fam = r.chance(0.5) ? '"Arial Narrow", Arial, sans-serif' : 'Impact, sans-serif';
  const size = fitFont(ctx, text, w * 0.9, h * 0.52, fam, '700');
  ctx.font = `700 ${size}px ${fam}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  for (const [lw, a, c] of [[size * 0.24, 0.13, ink], [size * 0.11, 0.36, ink],
    [size * 0.05, 0.85, ink], [size * 0.018, 1, '#ffffff']]) {
    ctx.globalAlpha = a; ctx.strokeStyle = c; ctx.lineWidth = lw;
    ctx.strokeText(text, x0 + w / 2, y0 + h / 2);
  }
  ctx.globalAlpha = 1;
  return { ink, kind: 'fascia', dead: false };
}

// ---------------------------------------------------------------------------
// The atlas
// ---------------------------------------------------------------------------
/**
 * Rasterise 64 signs into one canvas. Cell aspect is square in the atlas; the QUAD chooses the real
 * aspect, so a blade cell drawn tall and used on a tall quad reads correctly.
 */
export function buildSignAtlas() {
  const cv = document.createElement('canvas');
  cv.width = cv.height = ATLAS_DIM;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#05070a';
  ctx.fillRect(0, 0, ATLAS_DIM, ATLAS_DIM);

  const cells = [];
  for (let i = 0; i < GRID * GRID; i++) {
    const r = rngFor(`sign.cell.${i}`);
    const cx = (i % GRID) * CELL, cy = Math.floor(i / GRID) * CELL;
    ctx.save();
    ctx.beginPath(); ctx.rect(cx, cy, CELL, CELL); ctx.clip();
    ctx.fillStyle = '#05070a'; ctx.fillRect(cx, cy, CELL, CELL);

    // Kind distribution: mostly fascia and neon, which is what a real street is made of.
    const kind = r.weighted(['neon', 'fascia', 'box', 'blade', 'holo'], [0.30, 0.26, 0.20, 0.16, 0.08]);
    let info;
    if (kind === 'neon') info = drawNeon(ctx, r, cx + 8, cy + 8, CELL - 16, CELL - 16);
    else if (kind === 'fascia') info = drawFascia(ctx, r, cx, cy, CELL, CELL);
    else if (kind === 'box') info = drawBox(ctx, r, cx, cy, CELL, CELL);
    else if (kind === 'blade') info = drawBlade(ctx, r, cx, cy, CELL, CELL);
    else info = drawHolo(ctx, r, cx, cy, CELL, CELL);
    ctx.restore();

    const c = new THREE.Color(info.ink);
    cells.push({
      i, kind: info.kind, dead: info.dead,
      u0: cx / ATLAS_DIM, v0: 1 - (cy + CELL) / ATLAS_DIM,
      u1: (cx + CELL) / ATLAS_DIM, v1: 1 - cy / ATLAS_DIM,
      color: c.getHex(),
    });
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return { tex, cells };
}

/** Cells of a given kind, for pickers that need a specific shape of sign. */
export function cellsOfKind(cells, kind) {
  return cells.filter((c) => c.kind === kind);
}

// ---------------------------------------------------------------------------
// Material
// ---------------------------------------------------------------------------
/**
 * One material for every sign in the city.
 *
 * `aSign = (flickerPhase, flickerKind, holoFlag)`:
 *   flickerKind 0 = steady, 1 = slow breathe, 2 = bad ballast stutter, 3 = failing/half dead.
 * Flicker is computed from a time uniform in the shader, so a hundred signs cost one uniform update.
 */
export function makeSignMaterial(tex) {
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    emissiveMap: tex,
    emissive: 0xffffff,
    emissiveIntensity: 1.0,
    color: 0x0b0d11,
    roughness: 0.42,
    metalness: 0.0,
    transparent: true,
    alphaTest: 0.02,
    side: THREE.DoubleSide,
  });

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uSignTime = { value: 0 };
    mat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 aSign;\nvarying vec3 vSign;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSign = aSign;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uSignTime;
        varying vec3 vSign;
        float sg_hash(float x){ return fract(sin(x * 91.7) * 47453.1); }`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        float sPh = vSign.x;
        int sKind = int(vSign.y + 0.5);
        float t = uSignTime + sPh * 37.0;
        float f = 1.0;
        if (sKind == 1) {
          // Slow breathe -- an old transformer that cannot hold a steady load.
          f = 0.86 + 0.14 * sin(t * 1.7) * sin(t * 0.61);
        } else if (sKind == 2) {
          // Bad ballast: mostly on, with hard sub-frame dropouts. The dropout has to be SHORT --
          // a slow blink reads as a decoration, a 40 ms stutter reads as broken.
          float g = sg_hash(floor(t * 22.0));
          f = g < 0.09 ? 0.10 : (g < 0.14 ? 0.62 : 1.0);
        } else if (sKind == 3) {
          // Failing: long dead stretches, then a burst of life.
          float slow = sg_hash(floor(t * 0.7));
          float fast = sg_hash(floor(t * 19.0));
          f = slow < 0.30 ? (fast < 0.5 ? 0.06 : 0.55) : 1.0;
        }
        // Holographic panels drift and scan rather than flicker.
        if (vSign.z > 0.5) {
          f *= 0.72 + 0.28 * sin(vMapUv.y * 220.0 - t * 6.0);
          f *= 0.88 + 0.12 * sin(t * 2.3);
        }
        totalEmissiveRadiance *= f * 2.35;`);
  };
  mat.customProgramCacheKey = () => 'np-sign-v1';
  return mat;
}

/** Flicker kind for a sign, hashed so it is stable across rebuilds. */
export function flickerFor(key) {
  const h = hashFloat(`sign.flick.${key}`);
  return h < 0.62 ? 0 : h < 0.80 ? 1 : h < 0.93 ? 2 : 3;
}
